import useGetPrompts from '@/hooks/queries/prompts/useGetPrompts';
import { useCreateOrUpdateVariable } from '@/hooks/queries/variables';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { TextVariablePayload } from '@/types/variable';
import {
  stripTenantVariablePrefix,
  toTenantVariableTitle,
  validateTenantVariableTitle,
  validateTenantVariableTitleUnique,
} from '@/utils/promptUtils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ScrollShadow,
  Select,
  SelectItem,
} from '@nextui-org/react';
import '@uiw/react-markdown-preview/markdown.css';
import '@uiw/react-md-editor/markdown-editor.css';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const MDEditor = lazy(() => import('@uiw/react-md-editor'));

// Custom toolbar buttons for lists
const bulletList = {
  name: 'bulletList',
  keyCommand: 'bulletList',
  buttonProps: { 'aria-label': 'Add bullet list' },
  icon: (
    <svg viewBox="0 0 16 16" width="12px" height="12px">
      <path
        fill="currentColor"
        d="M2 4a1 1 0 100-2 1 1 0 000 2zm3.75-1.5a.75.75 0 000 1.5h8.5a.75.75 0 000-1.5h-8.5zm0 5a.75.75 0 000 1.5h8.5a.75.75 0 000-1.5h-8.5zm0 5a.75.75 0 000 1.5h8.5a.75.75 0 000-1.5h-8.5zM3 8a1 1 0 11-2 0 1 1 0 012 0zm-1 6a1 1 0 100-2 1 1 0 000 2z"
      />
    </svg>
  ),
  execute: (state: any, api: any) => {
    let modifyText = `\n- ${state.selectedText || 'List item'}\n`;
    api.replaceSelection(modifyText);
  },
};

const numberedList = {
  name: 'numberedList',
  keyCommand: 'numberedList',
  buttonProps: { 'aria-label': 'Add numbered list' },
  icon: (
    <svg viewBox="0 0 16 16" width="12px" height="12px">
      <path
        fill="currentColor"
        d="M2.003 2.5a.5.5 0 00-.723-.447l-1.003.5a.5.5 0 00.446.895l.28-.14V6H.5a.5.5 0 000 1h2.006a.5.5 0 100-1h-.503V2.5zM5 3.25a.75.75 0 01.75-.75h8.5a.75.75 0 010 1.5h-8.5A.75.75 0 015 3.25zm0 5a.75.75 0 01.75-.75h8.5a.75.75 0 010 1.5h-8.5A.75.75 0 015 8.25zm0 5a.75.75 0 01.75-.75h8.5a.75.75 0 010 1.5h-8.5a.75.75 0 01-.75-.75zM.924 10.32l.003-.004a.851.851 0 01.144-.153A.66.66 0 011.5 10c.195 0 .306.068.374.146a.57.57 0 01.128.376c0 .453-.269.682-.8 1.078l-.035.025C.692 11.98 0 12.495 0 13.5a.5.5 0 00.5.5h2.003a.5.5 0 000-1H1.146c.132-.197.351-.372.654-.597l.047-.035c.47-.35 1.156-.858 1.156-1.845 0-.365-.118-.744-.377-1.038-.268-.303-.658-.484-1.126-.484-.48 0-.84.202-1.068.392a1.858 1.858 0 00-.348.384l-.007.011a.5.5 0 00.851.525zM.5 10.055l-.427-.26.427.26z"
      />
    </svg>
  ),
  execute: (state: any, api: any) => {
    let modifyText = `\n1. ${state.selectedText || 'List item'}\n`;
    api.replaceSelection(modifyText);
  },
};

const createVariableSchema = (
  existingVariableTitles: string[],
  editingTitle?: string | null
) =>
  z.object({
    parent_content_type: z.string().min(1, 'Content type is required'),
    parent_object_id: z.number().nullable().optional(),
    text_variables: z.array(
      z.object({
        title: z
          .string()
          .min(1, 'Variable name is required')
          .superRefine((title, ctx) => {
            const validation = validateTenantVariableTitle(title);
            if (!validation.isValid && validation.errorMessage) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: validation.errorMessage,
              });
              return;
            }

            const uniqueValidation = validateTenantVariableTitleUnique(
              title,
              existingVariableTitles,
              editingTitle
            );
            if (!uniqueValidation.isValid && uniqueValidation.errorMessage) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: uniqueValidation.errorMessage,
              });
            }
          }),
        value: z.string().min(1, 'Variable value is required'),
      })
    ),
  });

type VariableFormData = z.infer<ReturnType<typeof createVariableSchema>>;

const EMPTY_FORM_VALUES: VariableFormData = {
  parent_content_type: 'multitenant.tenant',
  parent_object_id: null,
  text_variables: [{ title: '', value: '' }],
};

interface AddVariablePanelProps {
  isOpen: boolean;
  onClose: () => void;
  editingVariable?: TextVariablePayload | null;
  existingVariableTitles?: string[];
  onSuccess?: () => void;
}

export function AddVariablePanel({
  isOpen,
  onClose,
  editingVariable,
  existingVariableTitles = [],
  onSuccess,
}: AddVariablePanelProps) {
  const { data: prompts } = useGetPrompts();
  const { mutate, isPending } = useCreateOrUpdateVariable({
    onSuccess: () => onSuccess?.(),
  });
  const { tenantId } = useActiveTenant();
  const [valueEditorKey, setValueEditorKey] = useState(0);

  const editingTitle = editingVariable?.text_variables[0]?.title ?? null;

  const variableSchema = useMemo(
    () => createVariableSchema(existingVariableTitles, editingTitle),
    [existingVariableTitles, editingTitle]
  );

  const defaultValues: VariableFormData = {
    ...EMPTY_FORM_VALUES,
    parent_object_id: tenantId ?? null,
  };

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<VariableFormData>({
    resolver: zodResolver(variableSchema),
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  // Reset form when opening the panel or switching add/edit mode
  useEffect(() => {
    if (!isOpen) return;

    if (editingVariable) {
      reset({
        parent_content_type: 'multitenant.tenant',
        parent_object_id: editingVariable.parent_object_id,
        text_variables: editingVariable.text_variables.map((variable) => ({
          ...variable,
          title: stripTenantVariablePrefix(variable.title),
        })),
      });
      return;
    }

    reset({
      ...EMPTY_FORM_VALUES,
      parent_object_id: tenantId ?? null,
    });
    setValueEditorKey((key) => key + 1);
  }, [isOpen, editingVariable, reset, tenantId]);

  // Set tenant ID when content type is tenant
  useEffect(() => {
    const contentType = watch('parent_content_type');
    if (contentType === 'multitenant.tenant' && tenantId) {
      setValue('parent_object_id', tenantId);
    }
  }, [watch('parent_content_type'), tenantId, setValue]);

  const parentContentType = watch('parent_content_type');
  const parentObjectId = watch('parent_object_id');
  const textVariables = watch('text_variables');
  const valueError = errors.text_variables?.[0]?.value;

  const onSubmit = (data: VariableFormData) => {
    mutate({
      data: {
        text_variables: data.text_variables.map((variable) => ({
          ...variable,
          title: toTenantVariableTitle(variable.title),
        })),
        parent_content_type: data.parent_content_type,
        parent_object_id: data.parent_object_id || null,
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
      classNames={{
        wrapper: '!items-start !justify-end',
        base: '!m-0 rounded-medium h-[calc(100vh-64px)]',
      }}
      size="md"
      motionProps={{
        variants: {
          enter: {
            x: 0,
            transition: { duration: 0.3 },
          },
          exit: {
            x: 800,
            transition: {
              duration: 0.2,
              ease: 'easeIn',
            },
          },
        },
      }}
    >
      <ModalContent className="absolute top-2 right-2 h-[calc(100vh-20px)]">
        {(modalClose) => (
          <>
            <ModalHeader className="flex justify-between items-center px-6 py-2 border-b">
              <span className="text-large font-semibold">
                {editingVariable
                  ? 'Edit Tenant Variable'
                  : 'Add Tenant Variable'}
              </span>
              <Button isIconOnly variant="light" onPress={modalClose}>
                <Icon icon="mdi:close" className="h-5 w-5" />
              </Button>
            </ModalHeader>

            <ModalBody className="overflow-hidden mt-2">
              <ScrollShadow className="h-[calc(100vh-250px)]">
                <form
                  id="add-tenant-variable-form"
                  onSubmit={handleSubmit(onSubmit)}
                  className="flex flex-col space-y-6 px-1"
                >
                  {parentContentType === 'agentos.prompt' && (
                    <Select
                      label="Select Prompt (Optional)"
                      value={parentObjectId?.toString() || ''}
                      onChange={(e) =>
                        setValue(
                          'parent_object_id',
                          e.target.value ? parseInt(e.target.value, 10) : null
                        )
                      }
                      selectedKeys={
                        parentObjectId ? [parentObjectId.toString()] : []
                      }
                      description="If not selected, this variable will be shared across all prompts of this content type"
                      classNames={{
                        description: 'text-gray-500 text-xs leading-relaxed',
                      }}
                      isRequired={false}
                    >
                      {(prompts?.results || []).map((prompt: any) => (
                        <SelectItem
                          key={prompt.id.toString()}
                          value={prompt.id.toString()}
                        >
                          {prompt.name}
                        </SelectItem>
                      ))}
                    </Select>
                  )}

                  <Input
                    label="Variable Name"
                    // placeholder="Enter variable name"
                    value={textVariables[0]?.title || ''}
                    onChange={(e) =>
                      setValue('text_variables.0.title', e.target.value, {
                        shouldValidate: true,
                      })
                    }
                    isInvalid={!!errors.text_variables?.[0]?.title}
                    errorMessage={errors.text_variables?.[0]?.title?.message}
                    description="No spaces or dots. Use underscores for multiple words (e.g. company_name, support_email)."
                    startContent={
                      <span className="text-default-400 text-sm pointer-events-none select-none">
                        tenant.
                      </span>
                    }
                    classNames={{
                      description: 'text-gray-500 text-xs leading-relaxed',
                    }}
                  />

                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="tenant-variable-value"
                      className="text-foreground text-small font-medium"
                    >
                      Value
                    </label>
                    <div
                      data-color-mode="light"
                      id="tenant-variable-value"
                      className={`rounded-medium overflow-hidden ${
                        valueError
                          ? 'ring-.5 ring-danger border border-danger'
                          : 'border border-default-200'
                      }`}
                    >
                      <Suspense fallback={<div>Loading editor...</div>}>
                        <MDEditor
                          key={
                            editingVariable
                              ? `edit-${editingVariable.text_variables[0]?.title ?? 'variable'}`
                              : `add-${valueEditorKey}`
                          }
                          value={textVariables[0]?.value || ''}
                          onChange={(value?: string) =>
                            setValue('text_variables.0.value', value || '', {
                              shouldValidate: true,
                            })
                          }
                          preview="edit"
                          height={200}
                          textareaProps={{
                            placeholder:
                              'Choose list type from toolbar: Bullet (•) or Numbered (1.)',
                            'aria-label': 'Variable value editor',
                          }}
                          commands={[bulletList, numberedList]}
                          extraCommands={[]}
                        />
                      </Suspense>
                    </div>
                    {valueError && (
                      <div className="text-danger text-xs">
                        {valueError.message}
                      </div>
                    )}
                  </div>
                </form>
              </ScrollShadow>
            </ModalBody>

            <ModalFooter className="justify-between border-t mt-4">
              <Button variant="bordered" onPress={modalClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                isLoading={isPending}
                onPress={() => {
                  void handleSubmit(onSubmit)();
                }}
              >
                {isPending
                  ? 'Saving...'
                  : editingVariable
                    ? 'Save changes'
                    : 'Create variable'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
